import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Apple,
  Smartphone,
  LayoutGrid,
  ArrowRight,
  CheckCircle2,
  Shield,
  Sparkles,
  GitBranch,
  MessageSquare,
} from "lucide-react";
import FAQSection from "../components/FAQSection";
import BlogSection from "../components/BlogSection";
import { AnimatedCard } from "../components/AnimatedCard";

const CALENDLY = "https://calendly.com/nvseeds/30min";

export const MobileAppDevelopment = () => {
  const mobileFaqs = [
    {
      question: "What is the average cost of mobile app development in 2026?",
      answer:
        "Costs vary by complexity. A simple MVP may start around 30,000 GBP, while complex enterprise apps with AI integration can exceed 150,000 GBP.",
    },
    {
      question: "Should I build for iOS or Android first?",
      answer:
        "In most cases, a cross-platform launch is recommended so you can ship on both ecosystems faster. If your audience is highly specific, such as premium enterprise users, iOS may be prioritized.",
    },
    {
      question: "How long does it take to build a custom mobile app?",
      answer:
        "A production-ready app typically takes 4 to 6 months from discovery to deployment. With agile delivery, a working prototype can usually be delivered within the first 4 weeks.",
    },
    {
      question: "Do you provide maintenance after launch?",
      answer:
        "Yes. We provide ongoing support for OS compatibility updates, security patching, and performance tuning across iOS and Android.",
    },
  ];

  const stats = [
    { label: "Successful Projects", value: "500+", sub: "Delivered globally" },
    { label: "Countries Served", value: "20+", sub: "Global delivery" },
    { label: "Client Satisfaction", value: "98%", sub: "CSAT-focused teams" },
    { label: "Speed to Market", value: "40%", sub: "Faster vs industry avg." },
  ];

  const phases = [
    { title: "Phase 1 · Utility", desc: "Apps behaved like portable websites." },
    { title: "Phase 2 · Ecosystem", desc: "Better integration, still OS-siloed." },
    { title: "Phase 3 · Intelligent Edge", desc: "On-device GenAI, privacy-aware." },
  ];

  const serviceCards = [
    {
      icon: Apple,
      title: "iOS App Development",
      body:
        "Swift and SwiftUI for high-LTV experiences. Metal, CoreML, and privacy-first architecture for ATT and sandbox requirements.",
    },
    {
      icon: Smartphone,
      title: "Android App Development",
      body:
        "Kotlin and Jetpack Compose for foldables and diverse hardware. WorkManager, biometrics, and encrypted storage for enterprise data.",
    },
    {
      icon: LayoutGrid,
      title: "Cross-Platform",
      body:
        "Flutter and React Native when speed and one codebase matter. Ideal for enterprise SaaS and rapid iteration without excess debt.",
    },
  ];

  const whyUs = [
    { icon: GitBranch, title: "Clean core", text: "Modular mobile frontends that stay maintainable as ERP and backends evolve." },
    { icon: Sparkles, title: "AI in the flow", text: "GenAI for voice entry, insights, and summarization where users already work." },
    { icon: Shield, title: "Security", text: "End-to-end thinking for data in transit, at rest, and on device." },
    { icon: CheckCircle2, title: "Quality", text: "Rigorous testing and performance work so releases stay stable at scale." },
  ];

  const processSteps = [
    { step: "01", title: "Discovery", desc: "Align on goals, users, and technical constraints with your team." },
    { step: "02", title: "Build & iterate", desc: "Agile sprints with visible progress and early builds you can test." },
    { step: "03", title: "Launch & evolve", desc: "Release, monitor, and extend features without rewriting the foundation." },
  ];

  return (
    <>
      <Helmet>
        <title>Mobile App Development Services | Novaseeds Technologies</title>
        <meta
          name="description"
          content="NV Seeds builds secure, scalable iOS, Android, and cross-platform mobile apps with AI-ready architecture and enterprise-grade delivery."
        />
      </Helmet>

      <div className="min-h-screen pt-28 pb-16 md:pb-24 bg-background">
        {/* Hero — reference: strong headline + CTAs + trust strip */}
        <section className="relative overflow-hidden border-b border-[#E2E8F0]/80 bg-gradient-to-b from-[#F8FAFC] via-background to-background">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(37,99,235,0.12),transparent)] pointer-events-none" />
          <div className="container relative mx-auto px-4 md:px-6 max-w-7xl pt-10 pb-14 md:pt-14 md:pb-20">
            <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Services · Mobile
            </p>
            <div className="max-w-4xl">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight leading-[1.08]">
                Mobile App{" "}
                <span className="text-gradient">Development</span>
              </h1>
              <p className="mt-5 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                Delivering secure, reliable, and fast mobile applications — native,
                cross-platform, and AI-ready — aligned with how your business actually
                operates in 2026.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] transition hover:bg-blue-700"
                >
                  Connect With Us
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={CALENDLY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#CBD5E1] bg-card px-7 py-3.5 text-sm font-semibold text-foreground shadow-sm transition hover:bg-[#F8FAFC]"
                >
                  Book An Appointment
                </a>
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 overflow-visible">
              {stats.map((s, i) => (
                <AnimatedCard
                  key={s.label}
                  index={i}
                  slideFrom="alternate"
                  className="rounded-2xl border border-[#E2E8F0] bg-card/90 backdrop-blur-sm p-4 md:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                >
                  <p className="text-2xl md:text-3xl font-bold text-foreground tabular-nums">
                    {s.value}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-foreground">{s.label}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{s.sub}</p>
                </AnimatedCard>
              ))}
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl space-y-16 md:space-y-20">
          {/* Opening */}
          <section className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start pt-4">
            <div className="lg:col-span-7 space-y-5">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Stop building digital paperweights
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                In 2026, the market does not need another generic application that
                behaves like a mobile website. If your software is not leveraging
                on-device AI, seamless cross-platform sync, and clean-core architecture,
                you are paying for technical debt in advance.
              </p>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                As a premier mobile app development company, NV Seeds builds
                high-performance engines for business outcomes. Whether you are a
                startup or an enterprise planning beyond legacy ERP constraints, your
                mobile strategy must be faster, smarter, and more resilient.
              </p>
            </div>
            <AnimatedCard
              index={0}
              className="lg:col-span-5 rounded-3xl border border-[#E2E8F0] bg-card p-6 md:p-8 shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
            >
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-4">
                Why NV Seeds
              </p>
              <ul className="space-y-4">
                {[
                  "ROI-focused product decisions, not feature clutter",
                  "End-to-end delivery from APIs to polished UX",
                  "Dedicated squads that embed with your roadmap",
                ].map((t) => (
                  <li key={t} className="flex gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-600 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </AnimatedCard>
          </section>

          {/* Phases */}
          <section>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                The landscape
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                The state of mobile in 2026
              </h2>
              <p className="mt-3 text-muted-foreground">
                Three phases — from utility to intelligent edge. We build for the
                third.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-4 overflow-visible">
              {phases.map((p, i) => (
                <AnimatedCard
                  key={p.title}
                  index={i}
                  className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 transition-colors hover:border-blue-200/80"
                >
                  <p className="text-sm font-semibold text-foreground">{p.title}</p>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </AnimatedCard>
              ))}
            </div>
            <p className="mt-8 text-center text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              At NV Seeds, we focus on ROI per interaction. We build apps that do not
              just occupy home-screen space; they generate measurable business value.
            </p>
          </section>

          {/* Service cards — reference: comprehensive solutions grid */}
          <section>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                Our services
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Comprehensive mobile solutions
              </h2>
              <p className="mt-3 text-muted-foreground">
                Native depth where it matters, cross-platform speed where it wins.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 overflow-visible">
              {serviceCards.map(({ icon: Icon, title, body }, i) => (
                <AnimatedCard
                  key={title}
                  index={i}
                  slideFrom="alternate"
                  hoverScale={1.04}
                  hoverLift={-10}
                  className="group rounded-3xl border border-[#E2E8F0] bg-card p-8 shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:shadow-xl hover:border-blue-200/60"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 border border-blue-100 text-blue-600 mb-5 transition-colors group-hover:bg-blue-100/80 group-hover:scale-110 duration-300">
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{title}</h3>
                  <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                    {body}
                  </p>
                </AnimatedCard>
              ))}
            </div>
          </section>

          {/* Comparison table */}
          <AnimatedCard
            index={0}
            hoverScale={1.012}
            hoverLift={-4}
            className="rounded-3xl border border-[#E2E8F0] bg-card p-6 md:p-10 shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
              Native vs cross-platform
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl">
              In 2026, the right choice is the one that gets you to market with the
              least technical debt. Here is a practical comparison.
            </p>
            <div className="overflow-x-auto rounded-xl border border-[#E2E8F0]">
              <table className="w-full text-sm md:text-base">
                <thead className="bg-[#F8FAFC]">
                  <tr>
                    <th className="text-left font-semibold text-foreground px-4 py-3 border-b border-[#E2E8F0]">Feature</th>
                    <th className="text-left font-semibold text-foreground px-4 py-3 border-b border-[#E2E8F0]">Native</th>
                    <th className="text-left font-semibold text-foreground px-4 py-3 border-b border-[#E2E8F0]">Cross-platform</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Performance</td>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Best</td>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Near-native</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Development time</td>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Longer (two codebases)</td>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Up to 40% faster (one codebase)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Maintenance</td>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Higher cost</td>
                    <td className="px-4 py-3 border-b border-[#E2E8F0]">Lower cost</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Best for</td>
                    <td className="px-4 py-3">GPU-heavy / specialized</td>
                    <td className="px-4 py-3">Enterprise SaaS & startups</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </AnimatedCard>

          {/* Process */}
          <section>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-2">
                How we work
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                A clear path from idea to production
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6 overflow-visible">
              {processSteps.map((s, i) => (
                <AnimatedCard
                  key={s.step}
                  index={i}
                  className="relative rounded-3xl border border-[#E2E8F0] bg-gradient-to-b from-card to-[#F8FAFC]/50 p-8"
                >
                  <span className="text-4xl font-bold text-blue-600/25">{s.step}</span>
                  <h3 className="mt-2 text-lg font-semibold text-foreground">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </AnimatedCard>
              ))}
            </div>
          </section>

          {/* Why work with us */}
          <section className="rounded-3xl border border-[#E2E8F0] bg-[#F8FAFC] p-8 md:p-12">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground tracking-tight">
                Why NV Seeds
              </h2>
              <p className="mt-3 text-muted-foreground">
                Transforming ideas into solutions — backend, mobile, and AI as one system.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 overflow-visible">
              {whyUs.map(({ icon: Icon, title, text }, i) => (
                <AnimatedCard
                  key={title}
                  index={i}
                  className="group rounded-2xl bg-card border border-[#E2E8F0] p-6 shadow-sm"
                >
                  <Icon className="h-8 w-8 text-blue-600 mb-3 transition-transform duration-300 group-hover:scale-110" strokeWidth={1.5} />
                  <h3 className="font-semibold text-foreground">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
                </AnimatedCard>
              ))}
            </div>
            <AnimatedCard
              index={4}
              hoverScale={1.02}
              hoverLift={-4}
              className="mt-10 rounded-2xl border border-blue-200 bg-blue-50/50 p-6 md:p-8"
            >
              <p className="font-medium text-foreground mb-2">The Clean Core philosophy</p>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                We help enterprises move heavy customization out of legacy ERP cores and into
                modular mobile experiences, enabling long-term maintainability and faster
                innovation cycles.
              </p>
              <p className="font-medium text-foreground mt-6 mb-2">AI integration</p>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                In 2026, an app without AI is just a form. We integrate GenAI agents for voice
                data entry, predictive insights, and context-aware summarization directly in mobile
                flows.
              </p>
            </AnimatedCard>
          </section>

          {/* Case study */}
          <AnimatedCard
            index={0}
            hoverScale={1}
            hoverLift={-8}
            className="grid lg:grid-cols-12 gap-8 items-stretch rounded-3xl border border-[#E2E8F0] bg-card overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
          >
            <div className="lg:col-span-7 p-8 md:p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-[#E2E8F0]">
              <p className="text-sm font-semibold text-blue-600 mb-2">Case study</p>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-6">
                Modernizing supply chain for a UK logistics firm
              </h2>
              <p className="text-muted-foreground mb-3">
                <span className="font-medium text-foreground">The challenge:</span> A team relied
                on a 10-year-old handheld workflow with 15-minute data-entry cycles per delivery.
              </p>
              <p className="text-muted-foreground mb-4">
                <span className="font-medium text-foreground">The solution:</span> NV Seeds built
                a cross-platform app integrated with legacy APIs and on-device OCR to scan waybills
                instantly.
              </p>
              <p className="font-medium text-foreground mb-2">The results</p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  Data entry: 15 minutes → 45 seconds per stop
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  ROI within 7 months
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  1,200 drivers deployed across the UK
                </li>
              </ul>
            </div>
            <div className="lg:col-span-5 flex flex-col bg-gradient-to-br from-blue-600 to-blue-800 p-8 md:p-10 text-white min-h-full">
              <MessageSquare className="h-10 w-10 opacity-90 mb-4" />
              <p className="text-lg font-semibold leading-snug">
                Your 2026 mobile strategy playbook
              </p>
              <ul className="mt-6 space-y-3 text-sm text-blue-100 leading-relaxed">
                <li>· Backend-agnostic architecture for scaling</li>
                <li>· Offline-first where connectivity is poor</li>
                <li>· Encryption for data in transit and at rest</li>
                <li>· Intuitive UX — no 50-page manual</li>
                <li>· Modular code for safe feature growth</li>
              </ul>
            </div>
          </AnimatedCard>

          {/* Bottom CTA strip */}
          <AnimatedCard
            index={0}
            hoverScale={1.02}
            hoverLift={-6}
            className="rounded-3xl border border-blue-200 bg-gradient-to-r from-blue-50 to-[#EFF6FF] px-6 py-10 md:px-12 md:py-14 text-center"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground">
              Let&apos;s build something meaningful
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              The gap between success and failure is usually execution. NV Seeds provides
              dedicated teams to ship production-ready mobile software.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] hover:bg-blue-700"
              >
                Book An Appointment
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-[#CBD5E1] bg-white px-8 py-3.5 text-sm font-semibold text-foreground hover:bg-[#F8FAFC]"
              >
                Contact us
              </Link>
            </div>
          </AnimatedCard>

          <BlogSection
            categorySlug="software-development-company"
            title="What's New In The Market"
            maxPosts={3}
          />
          <FAQSection faqs={mobileFaqs} title="Frequently Asked Questions" />
        </div>
      </div>
    </>
  );
};
